import { useCallback, useEffect, useReducer, useRef } from "react";
import { executeStageStream } from "../services/workflowStreamApi";
import {
  initializePhaseState,
  updatePhaseState,
  extractProgressToken,
  extractAnalysisToken,
  extractExecutiveSummaryToken,
  isExecutionComplete,
  getActivePhase,
  calculateOverallProgress,
} from "../utils/streamingEventParser";

// ─────────────────────────────────────────────────────────────────────────────
// State shape and reducer
// ─────────────────────────────────────────────────────────────────────────────

function initialState() {
  return {
    // Connection lifecycle — these three always transition together
    isStreaming: false,
    isConnected: false,
    error: null,

    // Phase tracking
    phaseState: initializePhaseState(),
    activePhase: null,
    overallProgress: 0,

    // Streamed text buffers
    progressText: "",
    analysisText: "",
    executiveSummaryText: "",

    // Raw event log (for export)
    allEvents: [],

    // Timing
    startTime: null,
    endTime: null,
    executionDuration: null,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "START": {
      return {
        ...initialState(),
        isStreaming: true,
        startTime: action.startTime,
      };
    }

    case "CONNECTED":
      return { ...state, isConnected: true };

    case "EVENT": {
      const { event, startTime } = action;
      const newPhaseState = updatePhaseState(state.phaseState, event);
      const activePhase = getActivePhase(newPhaseState);
      const overallProgress = calculateOverallProgress(newPhaseState);

      const progressToken = extractProgressToken(event) || "";
      const analysisToken = extractAnalysisToken(event) || "";
      const summaryToken = extractExecutiveSummaryToken(event) || "";

      const isComplete = isExecutionComplete(event);
      const isError = event.event_type === "error";

      const endTime = isComplete ? new Date() : state.endTime;
      const executionDuration =
        isComplete && startTime ? endTime - startTime : state.executionDuration;

      return {
        ...state,
        phaseState: newPhaseState,
        activePhase,
        overallProgress,
        progressText: state.progressText + progressToken,
        analysisText: state.analysisText + analysisToken,
        executiveSummaryText: state.executiveSummaryText + summaryToken,
        allEvents: [...state.allEvents, event],
        isStreaming: isComplete ? false : state.isStreaming,
        isConnected: isComplete ? false : state.isConnected,
        endTime,
        executionDuration,
        error: isError ? event.error || "An error occurred during execution" : state.error,
      };
    }

    case "COMPLETE": {
      if (state.endTime) {
        // Already recorded end time from the execution_complete event
        return { ...state, isStreaming: false, isConnected: false };
      }
      const endTime = new Date();
      return {
        ...state,
        isStreaming: false,
        isConnected: false,
        endTime,
        executionDuration: state.startTime ? endTime - state.startTime : state.executionDuration,
      };
    }

    case "ERROR":
      return {
        ...state,
        isStreaming: false,
        isConnected: false,
        error: action.message,
      };

    case "STOP": {
      const endTime = new Date();
      return {
        ...state,
        isStreaming: false,
        isConnected: false,
        endTime: state.endTime || endTime,
        executionDuration:
          state.executionDuration || (state.startTime ? endTime - state.startTime : null),
      };
    }

    case "RESET":
      return initialState();

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Custom hook for managing workflow streaming execution
 *
 * Handles SSE connection lifecycle, event parsing, state management,
 * and provides clean interface for React components.
 *
 * @param {string} workflowId - Workflow ID
 * @param {number} stageNumber - Stage number to execute
 * @returns {object} Hook state and methods
 */
export function useWorkflowStreamingExecution(workflowId, stageNumber) {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  // Ref to startTime so callbacks don't re-create on every tick
  const startTimeRef = useRef(null);
  const streamControllerRef = useRef(null);

  const handleEvent = useCallback((event) => {
    dispatch({ type: "EVENT", event, startTime: startTimeRef.current });
  }, []);

  const handleError = useCallback((err) => {
    dispatch({ type: "ERROR", message: err.message || "Streaming connection failed" });
  }, []);

  const handleConnectionOpen = useCallback(() => {
    dispatch({ type: "CONNECTED" });
  }, []);

  const handleComplete = useCallback(() => {
    dispatch({ type: "COMPLETE" });
  }, []);

  const startExecution = useCallback(
    async (
      userQuery,
      enableWebValidation = true,
      conversationId = null,
      reportType = "comprehensive"
    ) => {
      const startTime = new Date();
      startTimeRef.current = startTime;
      dispatch({ type: "START", startTime });

      try {
        const controller = executeStageStream(workflowId, stageNumber, {
          userQuery,
          enableWebValidation,
          conversationId,
          reportType,
          onEvent: handleEvent,
          onError: handleError,
          onComplete: handleComplete,
          onConnectionOpen: handleConnectionOpen,
        });
        streamControllerRef.current = controller;
      } catch (err) {
        handleError(err);
      }
    },
    [workflowId, stageNumber, handleEvent, handleError, handleComplete, handleConnectionOpen]
  );

  const stopExecution = useCallback(() => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }
    dispatch({ type: "STOP" });
  }, []);

  const resetExecution = useCallback(() => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }
    startTimeRef.current = null;
    dispatch({ type: "RESET" });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamControllerRef.current) {
        streamControllerRef.current.abort();
        streamControllerRef.current = null;
      }
    };
  }, []);

  // Safety timeout: if the stream is still open after 10 minutes with no
  // completion signal (e.g. abrupt disconnect, backend crash), force-stop
  // so the UI doesn't spin forever.
  useEffect(() => {
    if (!state.isStreaming) {
      return;
    }
    const timer = setTimeout(
      () => {
        dispatch({
          type: "ERROR",
          message: "Execution timed out — no response received for 10 minutes.",
        });
        if (streamControllerRef.current) {
          streamControllerRef.current.abort();
          streamControllerRef.current = null;
        }
      },
      10 * 60 * 1000
    );
    return () => clearTimeout(timer);
  }, [state.isStreaming]);

  const getPhaseData = useCallback(
    (phaseId) => state.phaseState[phaseId] || null,
    [state.phaseState]
  );

  const isComplete = useCallback(() => {
    if (state.isStreaming || !state.endTime) {
      return false;
    }
    return !Object.values(state.phaseState).some((phase) => phase.status === "error");
  }, [state.isStreaming, state.endTime, state.phaseState]);

  const getFormattedDuration = useCallback(() => {
    if (!state.executionDuration) {
      return "-";
    }
    const seconds = Math.floor(state.executionDuration / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  }, [state.executionDuration]);

  return {
    // State
    isStreaming: state.isStreaming,
    isConnected: state.isConnected,
    error: state.error,
    phaseState: state.phaseState,
    activePhase: state.activePhase,
    overallProgress: state.overallProgress,
    progressText: state.progressText,
    analysisText: state.analysisText,
    executiveSummaryText: state.executiveSummaryText,
    allEvents: state.allEvents,
    startTime: state.startTime,
    endTime: state.endTime,
    executionDuration: state.executionDuration,

    // Methods
    startExecution,
    stopExecution,
    resetExecution,
    getPhaseData,
    isComplete,
    getFormattedDuration,
  };
}

export default useWorkflowStreamingExecution;
